import {intlLocale} from '../../languages';
import React, {useEffect, useState} from 'react';
import {useLocation} from 'react-router';
import {connect, useDispatch} from 'react-redux';
import {Trans, useTranslation} from 'react-i18next';
import {
    Row,
    Form,
    ListGroup,
    Button,
    ButtonGroup,
    Spinner,
    Alert,
} from 'react-bootstrap';

import MapPrintPreview from '../MapPrintPreview';
import CitySearch from '../CitySearch';
import {POSTER_COLORS} from '../../generatorOptions';
import {
    PRELOAD_DATA,
    SET_COUNTRY,
    SET_REGION,
    SET_QUERY,
    ADD_QUERY_RESULT,
    SET_COLOR,
    SET_IS_HORIZONTAL_LAYOUT,
    SET_CANVAS_SIZE,
    POP_QUERY_RESULT,
    PREVIEW,
    SET_ERROR,
    SET_ANALYTICS,
} from '../../actions/generator';

import config from '../../config';
import {sendAnalyticsEvent, getAnalyticsContext} from "../../utils";
import {COOKIE_CONSENT_EVENT} from '../../cookieConsent';


const Generator = (props) => {
    const {
        mapRequestId,
        mapPrintPreview,
        showSpinner,

        countries,
        queryResults,
        error,

        country,
        region,
        query,
        selectedFeatures,
        color,
        isHorizontalLayout,
        canvasSizeId,
    } = props;

    const {t, i18n} = useTranslation();
    const countryNames = new Intl.DisplayNames([intlLocale(i18n.language)], {type: 'region'});
    const countryLabel = country => countryNames.of(country.iso_alpha_2_code) || country.name;
    const [view, setView] = useState('interior');
    const [customColor, setCustomColor] = useState('#7B5CFF');
    const [countryInfoOpen, setCountryInfoOpen] = useState(false);
    const [countryInfoHovered, setCountryInfoHovered] = useState(false);
    const dispatch = useDispatch();
    const location = useLocation();
    const canvasSizes = [
        {
            id: "A1",
            name: t("generator.size.largeName"),
            description: t("generator.size.largeDescription"),
            width: 7016,
            height: 9933
        },
        {
            id: "A2",
            name: t("generator.size.mediumName"),
            description: t("generator.size.mediumDescription"),
            width: 4961,
            height: 7016,
        },
        {
            id: "A3",
            name: t("generator.size.smallName"),
            description: t("generator.size.smallDescription"),
            width: 3508,
            height: 4961,
        },
    ];
    const colors = POSTER_COLORS;

    useEffect(() => {
        dispatch({type: PRELOAD_DATA})
    }, []);

    useEffect(() => {
        const captureAnalyticsContext = () => dispatch({
            type: SET_ANALYTICS,
            payload: getAnalyticsContext(window.location.search, document.referrer),
        });
        captureAnalyticsContext();
        window.addEventListener(COOKIE_CONSENT_EVENT, captureAnalyticsContext);
        return () => window.removeEventListener(COOKIE_CONSENT_EVENT, captureAnalyticsContext);
    }, [dispatch]);

    const handleCountryChange = (event) => {
        dispatch({
            type: SET_COUNTRY,
            payload: countries.find(country => country.iso_alpha_2_code === event.target.value)
        });
    };

    const handleRegionChange = (event) => {
        dispatch({
            type: SET_REGION,
            payload: country.regions.find(region => String(region.id) === event.target.value) || null
        });
    };

    const handleQuery = (event) => {
        dispatch({
            type: SET_QUERY,
            payload: event.target.value
        });
    };

    const handleAddQueryResult = (feature, name) => {
        dispatch({
            type: ADD_QUERY_RESULT,
            payload: {
                type: 'Feature',
                geometry: feature.geometry,
                properties: {place: feature.properties.place, name, query}
            }
        });
        dispatch({type: SET_QUERY, payload: ''});
    };

    const handlePopQueryResult = index => {
        dispatch({type: POP_QUERY_RESULT, payload: index});
    };

    const handleColorChange = (event) => {
        const data = event.currentTarget.dataset;
        dispatch({
            type: SET_COLOR,
            payload: {main: data.mainColor, text: data.textColor}
        });
    };

    const handleLayoutChange = (event) => {
        dispatch({
            type: SET_IS_HORIZONTAL_LAYOUT,
            payload: event.target.checked
        });
    };

    const handleSizeChange = (event) => {
        const data = event.currentTarget.dataset;
        dispatch({
            type: SET_CANVAS_SIZE,
            payload: {
                canvasSizeId: event.target.value,
                canvasWidth: parseInt(data.width),
                canvasHeight: parseInt(data.height),
            }
        });
    };

    const handlePreview = () => {
        dispatch({
            type: PREVIEW
        });
        sendAnalyticsEvent('previewButton');
    };

    const handleBuy = () => {
        sendAnalyticsEvent('buyButton');
        const checkoutLink = document.createElement('a');
        checkoutLink.href = `${config.apiHost}/map/api/map-requests/${mapRequestId}/checkout`;
        checkoutLink.rel = 'noreferrer';
        document.body.appendChild(checkoutLink);
        checkoutLink.click();
        checkoutLink.remove();
    }

    const handleCloseError = () => {
        dispatch({
            type: SET_ERROR,
            payload: null,
        })
    }

    return (
        <section className="builder-section" id="generator">
        <div className="design-container">
            {error && (
                <Row className="gx-0">
                    <Alert className="generator-alert" variant="primary" dismissible={true} onClose={handleCloseError} role="alert">
                        <span className="generator-alert-icon" aria-hidden="true"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 8v4m0 4h.01"/></svg></span>
                        <span>{t(error)}</span>
                    </Alert>
                </Row>
            )}
            <div className="builder-section-heading">
                <h2>{t('buttons.create')}</h2>
                <p>{t('generator.instructions')}</p>
            </div>
            <div className="builder-grid">
                <div className="preview-tabs" role="group" aria-label={t('design.previewView')}>
                    {['poster', 'interior'].map(id => <button key={id} type="button" disabled={showSpinner} aria-pressed={view === id} onClick={() => setView(id)}>{t(`design.${id}`)}</button>)}
                </div>
                <div className="builder-preview">
                    <MapPrintPreview
                        backgroundImageUrl={mapPrintPreview.backgroundImageUrl}
                        overlayImageUrl={mapPrintPreview.mapPrintPreviewImageUrl}
                        fullSizeImageUrl={mapPrintPreview.mapPrintPreviewFullImageUrl}
                        color={color}
                        showSpinner={showSpinner}
                        view={view}
                        isHorizontalLayout={isHorizontalLayout}
                        countryName={country ? countryLabel(country) : ''}
                        frameWidth={canvasSizes.find(size => size.id === canvasSizeId)[isHorizontalLayout ? 'height' : 'width'] * 0.08}
                        x={mapPrintPreview.x}
                        y={mapPrintPreview.y}
                    />
                </div>
                <div className="builder-panel">

                    <Form onSubmit={event => event.preventDefault()}>
                        <fieldset className="generator-parameters" disabled={showSpinner}>
                        <h2><Trans>generator.location</Trans></h2>
                        <Form.Group className="mb-3">
                            <div className="country-label-row">
                                <Form.Label htmlFor="country"><Trans>generator.location.country</Trans></Form.Label>
                                <div className="country-info"
                                    onMouseEnter={() => setCountryInfoHovered(true)} onMouseLeave={() => setCountryInfoHovered(false)}
                                    onBlur={event => {if (!event.currentTarget.contains(event.relatedTarget)) setCountryInfoOpen(false);}}
                                    onKeyDown={event => {if (event.key === 'Escape') {setCountryInfoOpen(false); setCountryInfoHovered(false);}}}>
                                    <button type="button" className="country-info-toggle" id="country-data-source" aria-label={t('design.countryDataSource')} aria-expanded={countryInfoOpen || countryInfoHovered} aria-controls="country-data-notice" disabled={showSpinner} onClick={() => setCountryInfoOpen(!countryInfoOpen)}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 11v6m0-10v.01"/></svg>
                                    </button>
                                    <div id="country-data-notice" className={`country-info-menu dropdown-menu${countryInfoOpen || countryInfoHovered ? ' show' : ''}`}>
                                        <p><Trans i18nKey="design.countryDataNotice" components={{osm: <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer"/>}}/></p>
                                    </div>
                                </div>
                            </div>
                            <Form.Select
                                id="country"
                                autoComplete="off"
                                disabled={showSpinner}
                                value={country?.iso_alpha_2_code || ''}
                                onChange={handleCountryChange}
                            >
                                {countries.map((country, index) => (
                                    <option key={index} value={country.iso_alpha_2_code}>{countryLabel(country)}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        {country && country.regions.length > 0 && (
                            <Form.Group className="mb-3">
                                <Form.Label htmlFor="region"><Trans>generator.location.region</Trans></Form.Label>
                                <Form.Select
                                    id="region"
                                    autoComplete="off"
                                    disabled={showSpinner}
                                    value={region?.id || ''}
                                    onChange={handleRegionChange}
                                >
                                    <option key={-1} value=""><Trans>generator.location.region.wholeCountry</Trans></option>
                                    {country.regions.map((region, index) => (
                                        <option key={index} value={region.id}>{region.name_en}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        )}
                        <CitySearch
                            query={query}
                            results={queryResults}
                            selected={selectedFeatures}
                            disabled={showSpinner}
                            onQueryChange={handleQuery}
                            onAdd={handleAddQueryResult}
                            onRemove={handlePopQueryResult}
                        />
                        <Form.Group className="mb-3">
                            <h2><Trans>generator.style</Trans></h2>
                            <div className="color-palette">
                                <div className="color-palette-options">
                                    <label className="custom-color" style={{background: colors.some(item => item.main === color.main) ? undefined : customColor}}>
                                        <input type="color" aria-label={t('design.customColor')} value={customColor} disabled={showSpinner} onChange={event => {
                                            const value = event.target.value;
                                            setCustomColor(value);
                                            const rgb = value.slice(1).match(/.{2}/g).map(part => parseInt(part, 16));
                                            const text = rgb[0] * 0.299 + rgb[1] * 0.587 + rgb[2] * 0.114 > 153 ? '#000000' : '#ffffff';
                                            dispatch({type: SET_COLOR, payload: {main: value, text}});
                                        }}/>
                                        <span aria-hidden="true">+</span>
                                    </label>
                                {colors.map(item => (
                                        <button
                                            key={item.id}
                                            type="button"
                                            aria-label={t(`design.color.${item.id}`)}
                                            aria-pressed={item.main === color.main}
                                            className={item.main === color.main ? "color-picker color-picker-active": "color-picker"}
                                            style={{"backgroundColor": item.main, "borderColor": item.main === '#ffffff' ? 'var(--design-input)' : item.main}}
                                            data-main-color={item.main}
                                            data-text-color={item.text}
                                            onClick={handleColorChange}
                                            disabled={showSpinner}
                                        />
                                ))}
                                </div>
                            </div>
                            <p className="style-hint">{t('design.styleHint')}</p>
                            <Form.Switch className="orientation-switch mt-2">
                                <Form.Switch.Input
                                    id="isHorizontalLayout"
                                    disabled={showSpinner}
                                    checked={isHorizontalLayout}
                                    onChange={handleLayoutChange}
                                />
                                <Form.Switch.Label htmlFor="isHorizontalLayout">
                                    <Trans>generator.style.horizontalLayout</Trans>
                                </Form.Switch.Label>
                            </Form.Switch>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <h2><Trans>generator.size</Trans></h2>
                            <ListGroup className="list-group-radio position-relative gap-2 border-0">
                                {canvasSizes.map((size, index) => (
                                    <div key={index} className="position-relative">
                                        <Form.Check>
                                            <Form.Check.Input
                                                className="position-absolute top-50 end-0 me-3 fs-5"
                                                type="radio"
                                                name="canvasSize"
                                                id={"canvasSize" + size.id}
                                                value={size.id}
                                                data-width={size.width}
                                                data-height={size.height}
                                                checked={canvasSizeId === size.id}
                                                onChange={handleSizeChange}
                                                disabled={showSpinner}
                                            />
                                            <ListGroup.Item
                                                as="label"
                                                htmlFor={"canvasSize" + size.id}
                                                className="pt-1 pb-1"
                                            >
                                                <strong className="fw-medium">{size.name} · {size.id}</strong>
                                                <span className="d-block small opacity-75">
                                                {size.description.replace(/^A[123],?\s*/, '')}
                                            </span>
                                            </ListGroup.Item>
                                        </Form.Check>
                                    </div>
                                ))}
                            </ListGroup>
                        </Form.Group>
                        </fieldset>
                        <Form.Group className="mb-0">
                            {showSpinner ? (
                                <>
                                    <Button id="loadingButton" variant="primary" size="lg" className="w-100 mb-2" disabled>
                                        <Spinner animation="border" size="sm"/>&nbsp;<Trans>generator.loading</Trans>
                                    </Button>
                                    <small className="text-body-secondary fw-light">
                                        <Trans>generator.loading.note</Trans>
                                    </small>
                                </>
                            ) : mapRequestId ? (
                                <>
                                    <ButtonGroup className="w-100 mb-2">
                                        <Button id="buyButton" variant="info" size="lg" onClick={handleBuy}>
                                            <Trans i18nKey="generator.buy" values={{ price: country.formattedPrice }} />
                                        </Button>
                                    </ButtonGroup>
                                    <small className="text-body-secondary fw-light">
                                        <Trans>generator.buy.note</Trans>
                                    </small>
                                </>
                            ) : (
                                <Button
                                    id="previewButton"
                                    disabled={countries.length <= 0}
                                    variant="primary"
                                    size="lg"
                                    onClick={handlePreview}
                                    className="w-100"
                                >
                                    {t('generator.preview')}
                                </Button>
                            )}
                        </Form.Group>
                    </Form>
                </div>
            </div>
        </div>
        </section>
    );
};

const mapStateToProps = (state) => {
    return {
        mapRequestId: state.generator.mapRequestId,
        mapPrintPreview: state.generator.mapPrintPreview,
        showSpinner: state.generator.showSpinner,
        error: state.generator.error,

        countries: state.generator.countries,
        queryResults: state.generator.queryResults,

        country: state.generator.country,
        region: state.generator.region,
        query: state.generator.query,
        selectedFeatures: state.generator.selectedFeatures,
        color: state.generator.color,
        isHorizontalLayout: state.generator.isHorizontalLayout,
        canvasSizeId: state.generator.canvasSizeId,
    };
};

export default connect(mapStateToProps)(Generator);
